// Types
import type { ComponentPropsWithRef, ElementType, ForwardedRef } from 'react';
import { forwardRef } from 'react';

import clsx from 'clsx';

//libs
import { LG, MD, SM, XL, XS } from '@/types/utils/sizesType';

import Icon from '@/components/base/Icon';
//components
import Loader from '@/components/base/Loader';

const defaultClassName = clsx(
    'flex items-center font-GSemiBold leading-none max-w-full transition duration-100 outline-none focus:outline-none',
    'disabled:bg-icoGray-300 dark:disabled:bg-white/5 disabled:text-icoGray-400 dark:disabled:text-icoDarkMode-wolf disabled:cursor-not-allowed'
);

export const ButtonSize = {
    [XS]: 'h-[1.625rem] text-xxs px-2 rounded-m',
    ['h-38-rounded-full']: 'text-xs px-5 py-2 h-[38px] rounded-full',
    [SM]: 'h-8 min-w-[6.25rem] text-xs px-3 rounded-m',
    [MD]: 'px-5 py-2 min-h-12 min-w-150 text-sm rounded-md',
    [LG]: 'px-10 py-2 min-h-12 min-w-150 text-sm rounded-md',
    [XL]: 'px-10 py-2 min-h-12 min-w-[16.25rem] text-sm rounded-md'
};

export const PRIMARY = 'primary';
export const SECONDARY = 'secondary';
export const FORWARD = 'forward';
export const ERROR = 'error';
export const ERROR_SECONDARY = 'errorSecondary';
export const WHITE = 'white';
export const WHITE_NO_BORDER = 'whiteNoBorder';
export const LIGHT = 'light';
export const NO_BACKGROUND = 'noBackground';
export const NEUTRAL = 'neutral';
export const GREEN = 'green';
export const PURPLE = 'purple';
export const WHITE_OPACITY_40 = 'whiteOpacity40';

export const ButtonStyle = {
    [PRIMARY]:
        'bg-icoBlue text-white hover:bg-icoBlue-400 focus:bg-icoBlue-300 focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2 focus:text-white',
    [SECONDARY]:
        'bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2',
    [FORWARD]:
        'bg-icoGreen-100 dark:bg-icoGreen/20 text-icoGreen focus:ring-2 focus:ring-icoGreen-200 focus:ring-offset-2',
    [ERROR]:
        'bg-icoRed-100 dark:bg-icoRed/20 text-icoRed focus:ring-2 focus:ring-icoRed-200 focus:ring-offset-2',
    [ERROR_SECONDARY]:
        'bg-icoRed hover:bg-icoRed-400 dark:bg-icoRed/20 text-white focus:ring-2 focus:ring-icoRed-200 focus:ring-offset-2',
    [WHITE]:
        'bg-white dark:bg-icoDarkMode-anthracite text-icoBlack dark:text-white border border-icoGray-300 hover:border-opacity-70 focus:border-opacity-70 focus:ring-2 focus:ring-icoBlue-200 dark:focus:ring-offset-icoDarkMode-anthracite focus:ring-offset-2',
    [WHITE_NO_BORDER]:
        'bg-white dark:bg-icoDarkMode-anthracite text-icoGray-500 dark:text-white hover:border-opacity-70 focus:border-opacity-70 focus:ring-2 focus:ring-icoBlue-200 dark:focus:ring-offset-icoDarkMode-anthracite focus:ring-offset-2',
    [LIGHT]:
        'bg-icoGray-300 dark:bg-icoDarkMode-deepBlack text-icoGray-500 dark:text-icoDarkMode-wolf hover:bg-icoGray-100 hover:dark:bg-icoDarkMode-deepBlack focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2',
    orange: 'bg-icoOrange dark:bg-icoOrange/20 text-white focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2',
    orange100:
        'bg-icoOrange-100 dark:bg-icoOrange/20 text-icoOrange focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2',
    orange200:
        'bg-icoOrange-200 dark:bg-icoOrange/20 text-icoOrange focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2',
    [NO_BACKGROUND]:
        'text-icoBlue border border-icoBlue focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2 disabled:border-transparent disabled:bg-transparent',
    [NEUTRAL]: 'disabled:bg-transparent',
    [GREEN]:
        'bg-icoGreen text-white hover:bg-icoGreen-400 focus:bg-icoGreen-300 focus:ring-2 focus:ring-icoGreen-200 focus:ring-offset-2 focus:text-white',
    [PURPLE]:
        'bg-icoPurple text-white hover:bg-icoPurple-400 focus:bg-icoPurple-300 focus:ring-2 focus:ring-icoPurple-200 focus:ring-offset-2 focus:text-white',
    noBackgroundGray: 'border border-icoGray-300 dark:border-icoDarkMode-beton',
    [WHITE_OPACITY_40]:
        'bg-white/40 dark:bg-icoDarkMode-deepBlack/40 text-icoGray-500 dark:text-white'
} as const;

export type ButtonProps<TAs extends ElementType = 'button'> = {
    as?: TAs;
    justify?: string;
    children?: React.ReactNode;
    size?: keyof typeof ButtonSize;
    variant?: keyof typeof ButtonStyle;
    isLoading?: boolean;
    icon?: string;
    disabled?: boolean;
};

const UnwrappedButton = <TAs extends ElementType>(
    props: ButtonProps<TAs> &
        DistributiveOmit<ComponentPropsWithRef<ElementType extends TAs ? 'button' : TAs>, 'as'>,
    ref: ForwardedRef<any>
) => {
    const {
        as: Component = 'button',
        justify = 'center',
        size = 'md',
        variant = 'primary',
        isLoading = false,
        icon,
        className,
        disabled = false,
        ...restProps
    } = props;

    const isDisabled = disabled || isLoading;
    return (
        <Component
            ref={ref}
            className={clsx(className, defaultClassName, `justify-${justify}`, ButtonSize[size], {
                [ButtonStyle[variant]]: !isDisabled,
                'pointer-events-none': isLoading
            })}
            disabled={isDisabled}
            {...restProps}>
            {isLoading ? (
                <Loader className="mr-3 h-3.5 w-3.5 text-icoGray-400 dark:text-icoDarkMode-wolf" />
            ) : null}
            {!isLoading && icon ? (
                <Icon icon={icon} className={clsx({ 'mr-3': size !== XS, 'mr-1': size === XS })} />
            ) : null}
            {restProps?.children}
        </Component>
    );
};

const fixedForwardRef = forwardRef as FixedForwardRef;
const Button = fixedForwardRef(UnwrappedButton);

export default Button;
